import React, { useEffect, useState } from "react";
import { Button, Col, Row } from "reactstrap";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { Olympic } from "../../components";
import { Paginated } from "../../models";
import { Paginator, TableSpinner } from "../../components";
import DeleteModal from "../../components/Common/DeleteModal";
import { OlympicDto, PaginateQuery, State } from "../../interfaces";
import { NoResultFound } from "../../components/Common";
import {
  deleteOlympic,
  deleteOlympicConsent,
  getOlympics,
} from "../../store/actions";
import { AddEditOlympicModal } from "./AddEditOlympicModal";

export const OlympicsPage = () => {
  //#region hooks
  const { t } = useTranslation();
  const { courseId } = useParams<any>();
  const dispatch = useDispatch();
  //#endregion

  //#region internal state
  const initState = new PaginateQuery(20);
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>({
    ...initState,
    filter: {
      ["course"]: courseId,
    },
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [olympics, setOlympics] = useState<OlympicDto[]>([]);
  //#endregion

  //#region redux state
  const {
    response: paginatedOlympics,
    loading: loadingOlympics,
    success: loadingOlympicsSuccess,
    error: loadingOlympicsError,
  } = useSelector(
    (state: any): State<Paginated<OlympicDto>> => state.olympic.olympics
  );

  const {
    loading: deleteOlympicLoading,
    error: deleteOlympicError,
    success: deleteOlympicSuccess,
  } = useSelector((state: any): State<any> => state.olympic.deleteOlympic);
  //#endregion

  //#region effects
  useEffect(() => {
    if (courseId) {
      dispatch(
        getOlympics({
          ...paginateQuery,
          filter: {
            ["course"]: courseId,
          },
        })
      );
    }
  }, [paginateQuery, courseId]);

  useEffect(() => {
    if (!loadingOlympics && loadingOlympicsError)
      toast.error(loadingOlympicsError, {
        autoClose: 3000,
      });
  }, [loadingOlympics]);

  useEffect(() => {
    if (!deleteOlympicLoading && deleteOlympicSuccess) {
      setDeletingItemId(null);
      setShowDeleteModal(false);
      dispatch(
        getOlympics({
          ...paginateQuery,
        })
      );
    }
  }, [deleteOlympicLoading]);

  useEffect(() => {
    if (!loadingOlympics && loadingOlympicsSuccess) {
      const _olympics = paginatedOlympics?.data || [];
      setOlympics(_olympics);
    }
  }, [loadingOlympics]);

  //#endregion

  //#region handler
  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  const onAddOlympic = () => {
    if (courseId) {
      setShowAddEditModal(true);
    }
  };

  const onEditOlympic = (id: string) => {
    if (courseId && id) {
      setEditingItemId(id);
      setShowAddEditModal(true);
    }
  };

  const onDeleteOlympic = (id: string) => {
    setDeletingItemId(id);
    setShowDeleteModal(true);
    dispatch(deleteOlympicConsent(id));
  };

  const onDeleteOlympicConfirm = () => {
    dispatch(deleteOlympic(deletingItemId as string));
    setDeletingItemId(null);
  };

  //#endregion

  return (
    <React.Fragment>
      <Row className="mt-3">
        <Col>
          <Row>
            <Col className="d-flex justify-content-start">
              <h4 className="d-flex ms-1">
                <i className="ri-trophy-line me-1"></i>
                {t("Olympics")}
              </h4>
            </Col>
            <Col className="d-flex justify-content-end">
              <Button color="success" onClick={onAddOlympic}>
                <i className="ri-add-fill me-1 align-bottom"></i>
                {t("addItem", { item: "olympic" })}
              </Button>
            </Col>
          </Row>
        </Col>
        <ToastContainer />
        <Col xxl={12}>
          <Row className="team-list grid-view-filter mt-2">
            {!loadingOlympics &&
              olympics &&
              olympics.length > 0 &&
              olympics.map((item: OlympicDto, key) => (
                <Col key={key}>
                  <Olympic
                    onEditOlympic={() => onEditOlympic(item.id)}
                    onDeleteOlympic={() => onDeleteOlympic(item.id)}
                    {...item}
                  />
                </Col>
              ))}
          </Row>
          {!loadingOlympics && olympics && olympics.length > 0 && (
            <Paginator
              itemsOnCurrentPage={olympics?.length || 0}
              itemsPerPage={paginatedOlympics?.meta?.itemsPerPage as number}
              totalItems={paginatedOlympics?.meta?.totalItems || 0}
              currentPage={paginatedOlympics?.meta?.currentPage || 1}
              totalPages={paginatedOlympics?.meta?.totalPages || 0}
              onChangePage={(pageToChange: number) =>
                onChangePage(pageToChange)
              }
            />
          )}
          {loadingOlympics && <TableSpinner />}
          {!loadingOlympics && (!olympics || olympics.length == 0) && (
            <NoResultFound />
          )}
        </Col>
      </Row>
      <AddEditOlympicModal
        courseId={courseId || ""}
        id={editingItemId}
        show={showAddEditModal}
        onCloseClick={() => {
          setEditingItemId(null);
          setShowAddEditModal(false);
          if (courseId) {
            dispatch(
              getOlympics({
                ...paginateQuery,
                filter: {
                  ["course"]: courseId,
                },
              })
            );
          }
        }}
      />
      <DeleteModal
        loading={deleteOlympicLoading}
        show={showDeleteModal}
        onDeleteClick={() => onDeleteOlympicConfirm()}
        onCloseClick={() => {
          setShowDeleteModal(false);
        }}
        error={deleteOlympicError}
      />
    </React.Fragment>
  );
};
